import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { Button, Card } from '@/components';
import { apiJson } from '@/lib/api';
import { FiCheckCircle, FiSmartphone } from 'react-icons/fi';

function PurchaseSuccessPage() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const { session_id } = Route.useSearch();

  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');

  // Track screen view
  useEffect(() => {
    track('Purchase Success Screen', { session_id });
  }, [session_id]);

  // Verify subscription status
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    const verifySubscription = async () => {
      try {
        await apiJson(getToken, '/v1/subscription/verify', {
          method: 'POST',
        });
        track('Purchase Success Verified');
        // Track the complete one-time purchase conversion event
        track('One-Time Purchase Completed', { 
          session_id,
          source: 'day_7_email' 
        });
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[Purchase Success] Verification error:', err);
        // Still show success - webhook should have updated it
        setError(errorMessage);
        track('Purchase Success Verification Error', { error: errorMessage });
      } finally {
        setIsVerifying(false);
      }
    };

    verifySubscription();
  }, [isLoaded, isSignedIn, getToken, session_id]);

  return (
    <div className='min-h-screen bg-sky-light flex items-center justify-center px-6'>
      <div className='max-w-md w-full'>
        <Card className='text-center py-12'>
          {isVerifying ? (
            <>
              <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4' />
              <p className='text-gray-600'>Verifying your purchase...</p>
            </>
          ) : (
            <>
              <FiCheckCircle className='w-16 h-16 text-green-500 mx-auto mb-6' />
              <h1
                className='text-3xl font-semibold text-gray-900 mb-4'
                style={{
                  fontFamily:
                    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                }}
              >
                Purchase Complete!
              </h1>
              <p className='text-lg text-gray-600 mb-8'>
                Thank you for your purchase. Your Harbor account has been upgraded.
              </p>

              {/* App Instructions */}
              <div className='bg-primary/5 rounded-xl p-6 mb-6'>
                <FiSmartphone className='w-8 h-8 text-primary mx-auto mb-3' />
                <h2 className='font-semibold text-gray-900 mb-2'>
                  Open the Harbor App
                </h2>
                <p className='text-sm text-gray-600 mb-4'>
                  Your purchase is now active. Open the Harbor mobile app to start
                  using all features.
                </p>
                <div className='flex flex-col gap-3'>
                  <a
                    href='https://apps.apple.com/app/harbor'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-block'
                  >
                    <img
                      src='https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1704067200'
                      alt='Download on the App Store'
                      className='h-10 mx-auto'
                    />
                  </a>
                  <a
                    href='https://play.google.com/store/apps/details?id=com.leakingsieve.harbor'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-block'
                  >
                    <img
                      src='https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'
                      alt='Get it on Google Play'
                      className='h-14 mx-auto'
                    />
                  </a>
                </div>
              </div>

              {error && (
                <div className='mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800 text-sm'>
                  {error}
                </div>
              )}

              <Button
                fullWidth
                onClick={() => navigate({ to: '/app/expenses' })}
              >
                Continue to Harbor
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/purchase/success')({
  component: PurchaseSuccessPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      session_id: (search.session_id as string) || undefined,
    };
  },
});
